import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["min", "max", "step"]
const _hoisted_2 = ["min", "max", "step"]

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

type Props = {
  to: number
  from: number
  min: number
  max: number
  step: number
  fromColor: string
  toColor: string
  middleColor: string
}

type Emits = {
  (e: 'updateValueFrom', number: number): void
  (e: 'updateValueTo', number: number): void
  (e: 'changeValue', value: { from: number; to: number }): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MinMaxRangeSlider',
  props: {
    to: {},
    from: {},
    min: {},
    max: {},
    step: {},
    fromColor: {},
    toColor: {},
    middleColor: {}
  },
  emits: ["updateValueFrom", "updateValueTo", "changeValue"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit
const props = __props

const toInputRef = ref<HTMLInputElement | null>(null)
const fromProp = computed(() => props.from)
const toProp = computed(() => props.to)
const fromValue = ref(props.from)
const toValue = ref(props.to)
const leftDrag = ref(false)
const dragging = ref(false)
const dragBoth = ref(false)
const dragBothDistance = ref(0)

const fromValueAsString = computed({
  get: () => fromValue.value.toString(),
  set: (value) => {
    fromValue.value = parseFloat(value)
  }
})

const toValueAsString = computed({
  get: () => toValue.value.toString(),
  set: (value) => {
    toValue.value = parseFloat(value)
  }
})

const middleBoxPosition = computed(() => {
  const position = fromValue.value
  const range = props.max - props.min
  const safePosition = Math.min(props.max, Math.max(props.min, position)) // Ensure it stays within the slider range
  return `${100 * ((safePosition - props.min) / range)}%`
})

const toBoxPosition = computed(() => {
  const position = toValue.value
  const range = props.max - props.min
  const safePosition = Math.min(props.max, Math.max(props.min, position)) // Ensure it stays within the slider range
  return `${100 * ((safePosition - props.min) / range)}%`
})

const background = computed(
  () =>
    `linear-gradient(
        to right,
        ${props.fromColor} ${middleBoxPosition.value},
        ${props.middleColor} ${middleBoxPosition.value},
        ${props.middleColor} ${toBoxPosition.value},
        ${props.toColor} ${toBoxPosition.value}
    )`
)

onMounted(() => {
  window.addEventListener('mouseup', onInputMouseUp)
  window.addEventListener('mousemove', onInputDrag)
})

onUnmounted(() => {
  window.removeEventListener('mouseup', onInputMouseUp)
  window.removeEventListener('mousemove', onInputDrag)
})

watch(fromProp, (newValue) => {
  fromValue.value = newValue
})

watch(toProp, (newValue) => {
  toValue.value = newValue
})

watch(toValue, (newValue) => {
  if (newValue < fromValue.value) {
    fromValue.value = newValue
  }
  if (newValue > props.max) {
    toValue.value = props.max
    emit('updateValueTo', props.max)
  } else {
    emit('updateValueTo', newValue)
  }
})
watch(fromValue, (newValue) => {
  if (newValue > toValue.value) {
    toValue.value = newValue
  }
  if (newValue < props.min) {
    fromValue.value = props.min
    emit('updateValueFrom', props.min)
  } else {
    emit('updateValueFrom', newValue)
  }
})

function calculateMouseValue(e: MouseEvent): number | undefined {
  if (!toInputRef.value) return
  const element = toInputRef.value
  const mx = e.clientX
  const inputLeft = element.getBoundingClientRect().left
  const inputRight = element.getBoundingClientRect().right
  const inputWidth = inputRight - inputLeft
  const mouseRelative = (mx - inputLeft) / inputWidth
  return mouseRelative * (props.max - props.min) + props.min
}

function calculateClosestValue(value: number): number {
  return Math.round(value / props.step) * props.step
}

function onInputMouseDown(e: MouseEvent) {
  const value = calculateMouseValue(e)
  if (value === undefined) return

  if (e.shiftKey) {
    dragBoth.value = true
    dragBothDistance.value = Math.abs(toValue.value - fromValue.value)
  } else {
    const toDx = Math.abs(toValue.value - value)
    const fromDx = Math.abs(fromValue.value - value)
    const closestValue = calculateClosestValue(value)

    if (toDx < fromDx) {
      leftDrag.value = false
      toValue.value = closestValue
    } else {
      leftDrag.value = true
      fromValue.value = closestValue
    }
  }
  dragging.value = true

  e.preventDefault()
}

function onInputMouseUp() {
  dragging.value = false
  dragBoth.value = false
  emit('changeValue', { from: fromValue.value, to: toValue.value })
}

function onInputDrag(e: MouseEvent) {
  if (!dragging.value) return
  const value = calculateMouseValue(e)
  if (value === undefined) return

  const closestValue = calculateClosestValue(value)

  if (dragBoth.value) {
    const from = calculateClosestValue(closestValue - dragBothDistance.value / 2)
    const to = calculateClosestValue(closestValue + dragBothDistance.value / 2)
    fromValue.value = Math.max(props.min, from)
    toValue.value = Math.min(props.max, to)
  } else {
    if (!leftDrag.value) {
      toValue.value = closestValue
    } else {
      fromValue.value = closestValue
    }
  }
}

function onInputScroll(e: Event) {
  const dy = calculateClosestValue((e as WheelEvent).deltaY / 10)
  if (fromValue.value < toValue.value || dy > 0) {
    toValue.value = calculateClosestValue(toValue.value + props.step * dy)
    fromValue.value = calculateClosestValue(fromValue.value - props.step * dy)
  }
  e.preventDefault()
  e.stopPropagation()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: "min-max-range-slider",
    class: "range-slider",
    onWheel: onInputScroll
  }, [
    _createElementVNode("div", {
      class: "box-container",
      style: _normalizeStyle({
        background: background.value
      })
    }, null, 4),
    _withDirectives(_createElementVNode("input", {
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fromValueAsString).value = $event)),
      type: "range",
      class: "left-slider",
      min: _ctx.min,
      max: _ctx.max,
      step: _ctx.step,
      "data-html2canvas-ignore": ""
    }, null, 8, _hoisted_1), [
      [_vModelText, fromValueAsString.value]
    ]),
    _withDirectives(_createElementVNode("input", {
      ref_key: "toInputRef",
      ref: toInputRef,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((toValueAsString).value = $event)),
      type: "range",
      class: "right-slider",
      min: _ctx.min,
      max: _ctx.max,
      step: _ctx.step,
      "data-html2canvas-ignore": "",
      onMousedown: onInputMouseDown
    }, null, 40, _hoisted_2), [
      [_vModelText, toValueAsString.value]
    ])
  ], 32))
}
}

})